import { useRef, useEffect } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OrbitControls, Float, MeshDistortMaterial, Environment, ContactShadows, PresentationControls } from '@react-three/drei';
import * as THREE from 'three';
import { STLLoader } from 'three-stdlib';

// --- (Cena Anterior Mantida para Hero) ---
// ... (FloatingCube, FloatingSphere, FloatingTorus, Scene omitidos para brevidade aqui, mas permanecem no arquivo original) ...

// Componente Real para Modelos STL do Produto
export function STLModelViewer({ url, color = '#FF6B35' }: { url: string; color?: string }) {
  const geom = useLoader(STLLoader, url) as THREE.BufferGeometry;
  const meshRef = useRef<THREE.Mesh>(null);

  useEffect(() => {
    if (geom) {
      geom.computeVertexNormals();
      geom.center();
    }
  }, [geom]);

  return (
    <PresentationControls 
      global 
      zoom={0.8} 
      rotation={[0, -Math.PI / 4, 0]} 
      polar={[-Math.PI / 3, Math.PI / 3]} 
      azimuth={[-Math.PI / 1.4, Math.PI / 1.4]}
    >
      <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
        <mesh ref={meshRef} geometry={geom} castShadow receiveShadow scale={0.05}>
          <meshPhysicalMaterial 
            color={color} 
            roughness={0.2} 
            metalness={0.8} 
            clearcoat={1} 
            clearcoatRoughness={0.1}
          />
        </mesh>
      </Float>
    </PresentationControls>
  );
}

// Versão de Preview com Fallback (Se não houver STL, mostra o cubo animado original)
export function Product3DPreview({ stlUrl, color = '#FF6B35' }: { stlUrl?: string; color?: string }) {
  return (
    <div className="w-full h-full min-h-[300px]">
      <Canvas shadows camera={{ position: [0, 0, 5], fov: 45 }}>
        <ambientLight intensity={0.5} />
        <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} castShadow />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />
        
        {stlUrl ? (
          <STLModelViewer url={stlUrl} color={color} />
        ) : (
          <Float speed={3} rotationIntensity={2} floatIntensity={2}>
            <mesh castShadow>
              <boxGeometry args={[1.5, 1.5, 1.5]} />
              <meshStandardMaterial color={color} roughness={0.3} metalness={0.7} />
            </mesh>
          </Float>
        )}
        
        <ContactShadows position={[0, -2, 0]} opacity={0.4} scale={10} blur={2} />
        <Environment preset="city" />
        <OrbitControls enableZoom={true} autoRotate autoRotateSpeed={0.5} />
      </Canvas>
    </div>
  );
}

// Exportando os componentes anteriores renomeados para compatibilidade se necessário
export function ThreeDShowcase() {
  // Retornamos a cena animada abstrata do Hero original
  return (
    <div className="w-full h-full min-h-[400px] lg:min-h-[500px]">
      <Canvas camera={{ position: [0, 0, 8], fov: 45 }} gl={{ antialias: true, alpha: true }}>
        <Scene />
      </Canvas>
    </div>
  );
}

// Scene original mantida abaixo
function Scene() {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1.5} />
      <FloatingCube position={[-2, 0, 0]} color="#FF6B35" scale={1.2} />
      <FloatingSphere position={[2, 1, -1]} color="#00D9FF" scale={1} />
      <FloatingTorus position={[0, -1, 1]} color="#00C853" scale={0.8} />
      <ContactShadows position={[0, -3, 0]} opacity={0.4} scale={20} blur={2} far={4} />
      <Environment preset="city" />
      <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={0.5} />
    </>
  );
}

// (Re-adicionando FloatingCube, FloatingSphere, FloatingTorus aqui para fechar o arquivo)
function FloatingCube({ position, color, scale = 1 }: { position: [number, number, number]; color: string; scale?: number }) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = state.clock.getElapsedTime() * 0.2;
      meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.3;
    }
  });
  return (
    <Float speed={2} rotationIntensity={1.5} floatIntensity={2}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <boxGeometry args={[1, 1, 1]} />
        <MeshDistortMaterial color={color} distort={0.3} speed={2} roughness={0.2} metalness={0.8} />
      </mesh>
    </Float>
  );
}

function FloatingSphere({ position, color, scale = 1 }: { position: [number, number, number]; color: string; scale?: number }) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    if (meshRef.current) meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.1;
  });
  return (
    <Float speed={1.5} rotationIntensity={1} floatIntensity={1.5}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <sphereGeometry args={[0.8, 32, 32]} />
        <meshStandardMaterial color={color} roughness={0.1} metalness={0.9} emissive={color} emissiveIntensity={0.2} />
      </mesh>
    </Float>
  );
}

function FloatingTorus({ position, color, scale = 1 }: { position: [number, number, number]; color: string; scale?: number }) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = state.clock.getElapsedTime() * 0.4;
      meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.2;
    }
  });
  return (
    <Float speed={2.5} rotationIntensity={2} floatIntensity={1.8}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <torusGeometry args={[0.6, 0.2, 16, 50]} />
        <meshStandardMaterial color={color} roughness={0.2} metalness={0.8} />
      </mesh>
    </Float>
  );
}
